import { useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Paper,
  Typography,
  TextField,
  ListItemButton,
  ListItemText,
  Divider,
  InputAdornment,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import type { ConversationTemplate } from "@rubi/api-generated/clientRs";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";

import ReportIcon from "@/common/ReportIcon";

// Status update cards show a clipped preview; anything longer gets a tooltip
const STATUS_UPDATE_PREVIEW_LENGTH = 80;

interface TemplateSelectorProps {
  templates: ConversationTemplate[];
  selectedTemplate: string | null;
  onTemplateSelect: (template: ConversationTemplate) => void;
}

const TemplateSelector = ({
  templates,
  selectedTemplate,
  onTemplateSelect,
}: TemplateSelectorProps) => {
  const { t } = useTranslation("message");
  const [templateSearch, setTemplateSearch] = useState("");
  const [statusUpdateExpanded, setStatusUpdateExpanded] = useState(true);
  const [onlineReportingExpanded, setOnlineReportingExpanded] = useState(true);
  const [publicReportsExpanded, setPublicReportsExpanded] = useState(true);
  const [partnerReportsExpanded, setPartnerReportsExpanded] = useState(true);
  const [communityRegistrationExpanded, setCommunityRegistrationExpanded] =
    useState(true);
  const [publicRegistryExpanded, setPublicRegistryExpanded] = useState(true);
  const [partnerRegistryExpanded, setPartnerRegistryExpanded] = useState(true);

  // Filter templates by search term, excluding hidden templates
  const filteredTemplates = templates.filter((template) => {
    if (template.is_hidden) return false;
    const searchLower = templateSearch.toLowerCase();
    const nameMatch = template.name.toLowerCase().includes(searchLower);
    const contentMatch = template.content?.some((c) =>
      c.content.toLowerCase().includes(searchLower),
    );
    return nameMatch || contentMatch;
  });

  // Group templates by type
  const statusUpdateTemplates = filteredTemplates.filter(
    (template) =>
      !template.is_managed_online_reporting && !template.is_managed_registry,
  );
  const onlineReportingTemplates = filteredTemplates.filter(
    (template) => template.is_managed_online_reporting,
  );
  const registryTemplates = filteredTemplates.filter(
    (template) => template.is_managed_registry,
  );

  // Sort function: by sort_order first, then supplemental last, then alphabetically
  const sortBySupplemental = (
    a: ConversationTemplate,
    b: ConversationTemplate,
  ) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }

    const aIsSupplemental = a.org_config_survey?.is_supplemental || false;
    const bIsSupplemental = b.org_config_survey?.is_supplemental || false;

    if (aIsSupplemental !== bIsSupplemental) {
      return aIsSupplemental ? 1 : -1;
    }

    return a.name.localeCompare(b.name);
  };

  // Further group online reporting templates by is_hidden
  const publicReportTemplates = onlineReportingTemplates
    .filter((template) => !template.org_config_survey?.is_hidden)
    .sort(sortBySupplemental);

  const partnerReportTemplates = onlineReportingTemplates
    .filter((template) => template.org_config_survey?.is_hidden)
    .sort(sortBySupplemental);

  // Further group registry templates by is_hidden
  const publicRegistryTemplates = registryTemplates
    .filter((template) => !template.org_config_survey?.is_hidden)
    .sort(sortBySupplemental);

  const partnerRegistryTemplates = registryTemplates
    .filter((template) => template.org_config_survey?.is_hidden)
    .sort(sortBySupplemental);

  const sortedStatusUpdateTemplates = [...statusUpdateTemplates].sort(
    (a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.name.localeCompare(b.name);
    },
  );

  // Helper function to render template card
  const renderTemplateCard = (template: ConversationTemplate) => {
    const surveyDetails = template.org_config_survey;
    const hasImage = surveyDetails?.image_url;
    const description = surveyDetails?.description || "";

    const templateCard = (
      <Box
        key={template.id}
        onClick={() => onTemplateSelect(template)}
        sx={{
          p: 1,
          border: "1px solid var(--divider)",
          borderRadius: 1,
          cursor: "pointer",
          bgcolor:
            selectedTemplate === template.id
              ? "action.selected"
              : "transparent",
          "&:hover": {
            bgcolor:
              selectedTemplate === template.id ? undefined : "action.hover",
          },
          transition: "background-color 0.2s",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
        }}
      >
        {hasImage && (
          <ReportIcon
            src={surveyDetails.image_url}
            alt={template.name}
            size={32}
          />
        )}
        <Typography
          variant="body2"
          sx={{
            fontWeight: selectedTemplate === template.id ? 600 : 400,
            fontSize: "0.75rem",
            textAlign: "left",
            lineHeight: 1.3,
          }}
        >
          {template.name}
        </Typography>
      </Box>
    );

    return description ? (
      <Tooltip
        key={template.id}
        title={
          <Box sx={{ maxWidth: 400, p: 0.5 }}>
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                strong: ({ children }) => (
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {children}
                  </Box>
                ),
                em: ({ children }) => (
                  <Box component="span" sx={{ fontStyle: "italic" }}>
                    {children}
                  </Box>
                ),
                ul: ({ children }) => (
                  <Box component="ul" sx={{ mt: 0.5, mb: 0.5, pl: 2 }}>
                    {children}
                  </Box>
                ),
                ol: ({ children }) => (
                  <Box component="ol" sx={{ mt: 0.5, mb: 0.5, pl: 2 }}>
                    {children}
                  </Box>
                ),
                li: ({ children }) => (
                  <Typography component="li" variant="body2">
                    {children}
                  </Typography>
                ),
              }}
            >
              {description}
            </ReactMarkdown>
          </Box>
        }
        arrow
        placement="top"
        enterDelay={1000}
      >
        {templateCard}
      </Tooltip>
    ) : (
      templateCard
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: 420,
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid var(--text-primary)",
        borderRadius: 0,
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* <Typography variant="h6" gutterBottom>
          Message Templates
        </Typography> */}
        <TextField
          fullWidth
          size="small"
          placeholder={t("templates.searchPlaceholder")}
          value={templateSearch}
          onChange={(e) => setTemplateSearch(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: "text.secondary", fontSize: "20px" }}
                />
              </InputAdornment>
            ),
            endAdornment: templateSearch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setTemplateSearch("")}
                  sx={{ color: "text.secondary" }}
                >
                  <ClearIcon sx={{ fontSize: "18px" }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
              backgroundColor: "action.hover",
              height: "44px",
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
                boxShadow: "none",
              },
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
              padding: "8px 12px",
              "&::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {/* Online Reporting Section */}
        {onlineReportingTemplates.length > 0 && (
          <>
            <ListItemButton
              onClick={() =>
                setOnlineReportingExpanded(!onlineReportingExpanded)
              }
              sx={{
                "&:hover": {
                  bgcolor: "divider",
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, fontSize: "1.5rem" }}
                  >
                    {t("templates.onlineReporting")}
                  </Typography>
                }
              />
              {onlineReportingExpanded ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </ListItemButton>
            <Collapse in={onlineReportingExpanded} timeout="auto" unmountOnExit>
              {/* Publicly Available Reports */}
              {publicReportTemplates.length > 0 && (
                <>
                  <ListItemButton
                    onClick={() =>
                      setPublicReportsExpanded(!publicReportsExpanded)
                    }
                    sx={{
                      pl: 4,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                        >
                          {t("templates.publiclyAvailable")}
                        </Typography>
                      }
                    />
                    {publicReportsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={publicReportsExpanded}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.5,
                        p: 1.5,
                        pl: 4,
                      }}
                    >
                      {publicReportTemplates.map(renderTemplateCard)}
                    </Box>
                  </Collapse>
                </>
              )}

              {/* Partner/Hidden Reports */}
              {partnerReportTemplates.length > 0 && (
                <>
                  <ListItemButton
                    onClick={() =>
                      setPartnerReportsExpanded(!partnerReportsExpanded)
                    }
                    sx={{
                      pl: 4,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                        >
                          {t("templates.partnerHiddenReports")}
                        </Typography>
                      }
                    />
                    {partnerReportsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={partnerReportsExpanded}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.5,
                        p: 1.5,
                        pl: 4,
                      }}
                    >
                      {partnerReportTemplates.map(renderTemplateCard)}
                    </Box>
                  </Collapse>
                </>
              )}
            </Collapse>
          </>
        )}
        {/* Community Registration Section */}
        {registryTemplates.length > 0 && (
          <>
            <ListItemButton
              onClick={() =>
                setCommunityRegistrationExpanded(!communityRegistrationExpanded)
              }
              sx={{
                "&:hover": {
                  bgcolor: "divider",
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, fontSize: "1.5rem" }}
                  >
                    {t("templates.communityRegistration")}
                  </Typography>
                }
              />
              {communityRegistrationExpanded ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </ListItemButton>
            <Collapse
              in={communityRegistrationExpanded}
              timeout="auto"
              unmountOnExit
            >
              {/* Publicly Available Registry */}
              {publicRegistryTemplates.length > 0 && (
                <>
                  <ListItemButton
                    onClick={() =>
                      setPublicRegistryExpanded(!publicRegistryExpanded)
                    }
                    sx={{
                      pl: 4,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                        >
                          {t("templates.publiclyAvailable")}
                        </Typography>
                      }
                    />
                    {publicRegistryExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={publicRegistryExpanded}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.5,
                        p: 1.5,
                        pl: 4,
                      }}
                    >
                      {publicRegistryTemplates.map(renderTemplateCard)}
                    </Box>
                  </Collapse>
                </>
              )}

              {/* Partner/Hidden Registry */}
              {partnerRegistryTemplates.length > 0 && (
                <>
                  <ListItemButton
                    onClick={() =>
                      setPartnerRegistryExpanded(!partnerRegistryExpanded)
                    }
                    sx={{
                      pl: 4,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                        >
                          {t("templates.partnerHiddenReports")}
                        </Typography>
                      }
                    />
                    {partnerRegistryExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={partnerRegistryExpanded}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.5,
                        p: 1.5,
                        pl: 4,
                      }}
                    >
                      {partnerRegistryTemplates.map(renderTemplateCard)}
                    </Box>
                  </Collapse>
                </>
              )}
            </Collapse>
          </>
        )}
        {/* Status Update Section */}
        {sortedStatusUpdateTemplates.length > 0 && (
          <>
            <ListItemButton
              onClick={() => setStatusUpdateExpanded(!statusUpdateExpanded)}
              sx={{
                "&:hover": {
                  bgcolor: "divider",
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, fontSize: "1.5rem" }}
                  >
                    {t("templates.statusUpdate")}
                  </Typography>
                }
              />
              {statusUpdateExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={statusUpdateExpanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  p: 2,
                }}
              >
                {sortedStatusUpdateTemplates.map((template) => {
                  const englishContent = template.content?.find(
                    (c) => c.lang === "en",
                  );
                  const previewContent =
                    englishContent?.content ||
                    template.content?.[0]?.content ||
                    "";
                  const isTruncated =
                    previewContent.length > STATUS_UPDATE_PREVIEW_LENGTH;

                  const templateCard = (
                    <Box
                      key={template.id}
                      onClick={() => onTemplateSelect(template)}
                      sx={{
                        p: 1.5,
                        border: "1px solid var(--divider)",
                        borderRadius: 1,
                        cursor: "pointer",
                        bgcolor:
                          selectedTemplate === template.id
                            ? "action.selected"
                            : "transparent",
                        "&:hover": {
                          bgcolor:
                            selectedTemplate === template.id
                              ? "action.selected"
                              : "action.hover",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight:
                            selectedTemplate === template.id ? 600 : 500,
                          fontSize: "0.875rem",
                          mb: 0.5,
                        }}
                      >
                        {template.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          fontSize: "0.75rem",
                          lineHeight: 1.4,
                        }}
                      >
                        {previewContent.slice(0, STATUS_UPDATE_PREVIEW_LENGTH)}
                        {isTruncated ? "..." : ""}
                      </Typography>
                    </Box>
                  );

                  // Only worth a tooltip when the preview actually hides text
                  return isTruncated ? (
                    <Tooltip
                      key={template.id}
                      title={
                        <Box sx={{ maxWidth: 400, p: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {previewContent}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      enterDelay={1000}
                    >
                      {templateCard}
                    </Tooltip>
                  ) : (
                    templateCard
                  );
                })}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default TemplateSelector;
